import { useEffect, useState } from "react";

import {
  ListContainer,
  ListContext,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  Page,
  PageRoute,
  SCHOOL_YEAR_ACTIVE_ID,
  SearchBar,
  buildPath,
  buildTemplatePath,
  pn,
} from "@compass/core";

import { ListFooter } from "components";
import {
  CollegeListDto,
  CollegeListResource,
  ListFilterBase,
  Permission,
  StudentFilter as StudentFilterModel,
  StudentListDto,
  StudentListFilter,
  StudentListResource,
  TenantInfo,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import { Outlet, useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import produce from "immer";
import { DeletedStudentListPageHeader } from "./DeletedStudentListPageHeader";
import { DeletedStudentTable } from "./DeletedStudentTable";
import { StudentFilter } from "pages/students/students/StudentFilter";

const routeName = "deletedStudents";

export const deletedStudentsListPageRoute: PageRoute = {
  handle: "Deleted Students",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const DeletedStudentListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<StudentListFilter>({
    sortObjects: [{ property: pn<StudentListDto>("fullName"), isDesc: false }],
    studentFilter: {
      schoolYears: [SCHOOL_YEAR_ACTIVE_ID],
    },
  });

  const [initListFilter] = useState<StudentListFilter>(listFilter);
  const [listItems, setListItems] = useState<StudentListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [tenantInfo, setTenantInfo] = useState<TenantInfo>();

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.students.getDeletedList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.students.getDeletedListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.textingConversations.getTenantTextingInfo().then((response) => {
      setTenantInfo(response);
    });
    ApiService.students.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<StudentListFilter>({
    maxWidth: "xl",
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <StudentFilter
        filter={listFilter.studentFilter || ({} as StudentFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.studentFilter = filter;
            })
          )
        }
      />
    ),
  });

  const getIdsMethods = () => ApiService.students.getIds(listFilter);

  return (
    <>
      <Page
        header={
          <DeletedStudentListPageHeader rootPermission={ROOT_PERMISSION} handle={deletedStudentsListPageRoute.handle} />
        }
      >
        <ListContainer
          fullHeight
          header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
          tableContainer={
            <DeletedStudentTable
              rootPermission={ROOT_PERMISSION}
              tenantTextingInfo={tenantInfo}
              tableContainerRef={tableContainerRef}
              students={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
